import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { inject } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { PresentApi } from '../../apis/present/present-api';
import { MapEventEntity } from '../../apis/event/map-event-entity';
import { Sport, SPORTS, sportLocaleKey } from '../../apis/event/sport';

// Fix Leaflet default icon paths broken by bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
    iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
    shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

const _toDateString = (d: Date): string => d.toISOString().slice(0, 10);

@inject(PresentApi, I18N)
export class MapView {
    public fromDate: string = _toDateString(new Date());
    public toDate: string = _toDateString(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
    public searchText: string = '';
    public selectedSport: Sport | null = null;
    public isLoading: boolean = false;
    public hasError: boolean = false;
    public eventCount: number = 0;

    private _map: L.Map = null;
    private _markers: L.Marker[] = [];
    private _events: MapEventEntity[] = [];
    public mapContainer: HTMLElement = null;

    constructor(private readonly _presentApi: PresentApi, private readonly _i18n: I18N) {}

    public get sportOptions(): { value: Sport | null; label: string }[] {
        return [
            { value: null, label: this._i18n.tr('ui:sport_filter_all') },
            ...SPORTS.filter(s => s !== Sport.NotSet).map(value => ({ value, label: this._i18n.tr(`ui:${sportLocaleKey(value)}`) })),
        ];
    }

    public async attached(): Promise<void> {
        this._initMap();
        await this._load();
    }

    public detached(): void {
        if (this._map) {
            this._map.remove();
            this._map = null;
        }
    }

    private _initMap(): void {
        this._map = L.map(this.mapContainer).setView([62.0, 15.0], 5);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 19,
        }).addTo(this._map);
    }

    private _clearMarkers(): void {
        for (const m of this._markers) {
            m.remove();
        }
        this._markers = [];
    }

    private _renderMarkers(): void {
        this._clearMarkers();
        for (const ev of this._events) {
            const eventUrl = `${window.location.origin}/${ev.code}`;
            const sportLabel = ev.sport && ev.sport !== Sport.NotSet ? this._i18n.tr(`ui:${sportLocaleKey(ev.sport)}`) : '';
            const popup = `
                <strong>${ev.title}</strong><br>
                ${ev.organizer ? `<span>${ev.organizer}</span><br>` : ''}
                ${sportLabel ? `<span>${sportLabel}</span><br>` : ''}
                <span>${this._formatDateRange(ev.startDate, ev.endDate)}</span><br>
                <a href="${eventUrl}" target="_blank">${ev.code}</a>
            `;
            const marker = L.marker([ev.latitude, ev.longitude])
                .addTo(this._map)
                .bindPopup(popup);
            this._markers.push(marker);
        }
    }

    private _formatDateRange(start: string, end: string): string {
        const s = start ? new Date(start).toLocaleDateString('sv-SE') : '';
        const e = end ? new Date(end).toLocaleDateString('sv-SE') : '';
        return s === e ? s : `${s} – ${e}`;
    }

    public async search(): Promise<void> {
        await this._load();
    }

    private async _load(): Promise<void> {
        this.isLoading = true;
        this.hasError = false;
        try {
            this._events = await this._presentApi.getMapEvents(
                this.fromDate || undefined,
                this.toDate || undefined,
                this.searchText.trim() || undefined,
                this.selectedSport ?? undefined
            );
            this.eventCount = this._events.length;
            this._renderMarkers();
            if (this._events.length > 0 && this._map) {
                const group = L.featureGroup(this._markers);
                this._map.fitBounds(group.getBounds().pad(0.2));
            }
        } catch {
            this.hasError = true;
        } finally {
            this.isLoading = false;
        }
    }
}
